import { useSyncExternalStore } from 'react';

const PING_URL = '/api/v1/health/live';
const PING_INTERVAL_MS = 10_000;
const DEBOUNCE_ONLINE_MS = 3_000; // wait 3s before declaring online (avoids flicker)

async function checkConnectivity(): Promise<boolean> {
  try {
    const res = await fetch(PING_URL, { method: 'HEAD', cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * One shared connectivity store for the whole app (POS-I63).
 *
 * Every component that needs the online flag subscribes to the SAME state, fed by one 10-second
 * health check plus the browser's own online/offline events. The previous per-component copy
 * compared against the `isOnline` value captured when its effect first ran, so once the health
 * check had failed the "back online" branch could never fire again unless the browser also
 * raised an `online` event (a restarted server or a router that came back quietly left the till
 * stuck offline, with sales waiting in the queue, until the page was reloaded).
 */
let online: boolean = typeof navigator === 'undefined' ? true : navigator.onLine;
const listeners = new Set<() => void>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

function emit() {
  listeners.forEach((l) => l());
}

function commit(next: boolean) {
  if (online === next) return;
  online = next;
  emit();
}

/** Record a reachability result. "Online" is debounced, "offline" is instant. */
export function reportConnectivity(reachable: boolean) {
  if (reachable) {
    if (online) {
      // Already online: drop a pending flip that is no longer needed.
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      return;
    }
    if (debounceTimer) return; // a flip to online is already scheduled
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      commit(true);
    }, DEBOUNCE_ONLINE_MS);
  } else {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    commit(false);
  }
}

function onBrowserOnline() {
  void checkConnectivity().then(reportConnectivity);
}
function onBrowserOffline() {
  reportConnectivity(false);
}

function start() {
  if (started) return;
  started = true;
  void checkConnectivity().then((ok) => commit(ok));
  window.addEventListener('online', onBrowserOnline);
  window.addEventListener('offline', onBrowserOffline);
  // The periodic ping covers the cases where the browser raises no event (server restart, router).
  pingTimer = setInterval(() => {
    void checkConnectivity().then(reportConnectivity);
  }, PING_INTERVAL_MS);
}

function stop() {
  if (!started) return;
  started = false;
  window.removeEventListener('online', onBrowserOnline);
  window.removeEventListener('offline', onBrowserOffline);
  if (pingTimer) clearInterval(pingTimer);
  if (debounceTimer) clearTimeout(debounceTimer);
  pingTimer = null;
  debounceTimer = null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

function getSnapshot() {
  return online;
}

/** Current connectivity without subscribing (for non-React callers and specs). */
export function getOnlineSnapshot(): boolean {
  return online;
}

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test hook: reset the store between specs. */
export function __resetOnlineStatusForTests(initial: boolean) {
  stop();
  listeners.clear();
  online = initial;
}
